import React from 'react'
import {
  CodeIcon,
  ShieldCheckIcon,
  GraduationCapIcon,
  RocketIcon,
  ZapIcon,
  LayersIcon,
  ServerIcon,
  UnlockIcon,
  UsersIcon,
  WandSparklesIcon,
  BriefcaseIcon,
  HandIcon,
  TargetIcon,
  RepeatIcon,
  MailIcon,
  MessageSquareIcon,
  MapPinIcon,
} from 'lucide-react'

export const homeServices = [
  {
    title: 'Product building with AI',
    description:
      'We build your digital product or MVP using AI-accelerated workflows, from idea to shippable.',
    link: '/product-building',
    icon: <CodeIcon className="w-7 h-7" />,
    delay: 0.1,
  },
  {
    title: 'Consulting, AI with data privacy and open source',
    description:
      'We help you adopt AI without giving away your data, locking into a vendor, or breaking compliance.',
    link: '/consulting',
    icon: <ShieldCheckIcon className="w-7 h-7" />,
    delay: 0.2,
  },
  {
    title: 'Training with AI',
    description:
      'We get your teams confident, safe, and genuinely productive with AI tools — from prompting fundamentals to embedding AI in everyday workflows.',
    link: '/training',
    icon: <GraduationCapIcon className="w-7 h-7" />,
    delay: 0.3,
  },
]

export const homeTestimonials = [
  {
    quote:
      'greenmango transformed how we build software. Their AI-accelerated workflows cut our MVP development time in half while maintaining exceptional code quality.',
    photoUrl:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    name: 'Sarah Jenkins',
    title: 'CTO',
    company: 'TechFlow Inc.',
  },
]

export const productBuildingServices = [
  {
    title: 'Full product build, from idea to shippable product',
    description:
      'We take your concept and build a production-ready digital product end-to-end using AI-accelerated workflows. You get a working, maintainable product, not a prototype that falls apart. Includes architecture design, code generation, testing, deployment, and documentation.',
    icon: <RocketIcon className="w-6 h-6" />,
    delay: 0.1,
  },
  {
    title: 'MVP sprint, validate your idea in days',
    description:
      'A rapid 1 to 4 week sprint to turn your idea into a functional MVP. We handle the design-to-code pipeline with AI so you can test your concept with real users, gather feedback, and decide whether to invest further, without burning months of dev time.',
    icon: <ZapIcon className="w-6 h-6" />,
    delay: 0.2,
  },
  {
    title: 'New features for your live product, from insight to production',
    description:
      'Already shipped, but your roadmap keeps growing? We design and build new features directly into your existing product, starting from a real user insight and ending in production-ready, maintainable code. Discovery, design, build, and iteration, not bolted-on demos that break under real usage.',
    icon: <LayersIcon className="w-6 h-6" />,
    delay: 0.3,
  },
]

export const consultingServices = [
  {
    title: 'Private AI infrastructure setup',
    description:
      'We design and connect you to the right infrastructure provider for a fully private AI stack. Open-source models (Llama, Mistral, Qwen, etc.) running on dedicated infrastructure, whether cloud-based or on-premise, managed by trusted providers. No data ever leaves your control. Full compliance with GDPR, HIPAA, or any regulatory requirement.',
    icon: <ServerIcon className="w-6 h-6" />,
    delay: 0.1,
  },
  {
    title: 'AI vendor independence strategy',
    description:
      'We audit your current AI dependencies and architect a modular, model-agnostic stack using open standards. You can switch between providers, run multiple models, or go fully self-hosted without rewriting your entire workflow. No more single-vendor lock-in, no more unpredictable pricing spikes.',
    icon: <UnlockIcon className="w-6 h-6" />,
    delay: 0.2,
  },
  {
    title: 'AI-accelerated development for existing teams',
    description:
      'Your team already builds, but they want to improve their delivery with AI. We embed into your workflow to supercharge your development with AI tooling, code generation, automated testing, refactoring, and CI/CD optimization, while keeping code quality and maintainability intact.',
    icon: <UsersIcon className="w-6 h-6" />,
    delay: 0.4,
  },
]

export const trainingServices = [
  {
    title: 'AI tool fundamentals for every team',
    description:
      'Hands-on onboarding to the AI tools your organization actually uses \u2014 ChatGPT, Claude, Copilot, and more. Your people learn effective prompting, how to judge what to trust, and where AI genuinely helps versus where it gets in the way. No prior experience needed.',
    icon: <WandSparklesIcon className="w-6 h-6" />,
    delay: 0.1,
  },
  {
    title: 'Role-specific AI workflows',
    description:
      'Practical sessions tailored to each function \u2014 support, marketing, operations, engineering, and more. We work from the tasks your teams do every day, so people leave with concrete workflows they can use the next morning, not generic theory they\'ll forget.',
    icon: <BriefcaseIcon className="w-6 h-6" />,
    delay: 0.2,
  },
  {
    title: 'Safe and responsible AI use',
    description:
      'The guardrails your teams need: data privacy, what not to paste into a chatbot, spotting hallucinations, and lightweight internal guidelines for using AI responsibly. Pairs naturally with our privacy-first consulting so AI adoption never puts your data or compliance at risk.',
    icon: <ShieldCheckIcon className="w-6 h-6" />,
    delay: 0.3,
  },
]

export const trainingApproach = [
  {
    icon: <HandIcon className="w-5 h-5" />,
    title: 'Hands-on, not theory',
    description:
      'People learn by doing real tasks with AI during the session, on the tools you already use.',
  },
  {
    icon: <TargetIcon className="w-5 h-5" />,
    title: 'Relevant to each role',
    description:
      'Sessions are shaped around what your teams actually do, so they leave with workflows for their own work.',
  },
  {
    icon: <RepeatIcon className="w-5 h-5" />,
    title: 'Built to stick',
    description:
      'Practical exercises and clear next steps so AI use becomes a habit, not a one-off workshop.',
  },
]

export const contactInfo = [
  {
    title: 'Email us',
    description: 'contact@greenmango.net',
    icon: <MailIcon className="w-6 h-6" />,
  },
  {
    title: 'Book a call',
    description:
      'Schedule a free 30-minute discovery call to discuss your needs.',
    icon: <MessageSquareIcon className="w-6 h-6" />,
  },
  {
    title: 'Location',
    description: 'From Singapore, remote-first, serving clients globally.',
    icon: <MapPinIcon className="w-6 h-6" />,
  },
]

